import type { CinematicSequence } from '../../../renderer/src/cinematic-sequence';
import type { Ride } from "../domain/ride";
import type { CameraPlatformId } from "../domain/camera-platform";
import type { CameraDistanceId } from "../domain/camera-distance";
import { videoFetch } from './video-http';
import { RENDER_RELEASE } from '../../../renderer/src/render-release.generated';
import { RENDER_RELEASE_HEADER, releaseHeader } from '../../../renderer/src/release-contract';
import type { VideoPresentation } from '../../../renderer/src/video-presentation';
import type { VideoGenerationMetrics } from '../../../shared/video-generation';
import type { TrackAppearance } from '../../../renderer/src/track-appearance';
import type { DepthOfFieldSettings } from '../../../renderer/src/depth-of-field';
import type { VideoFrameProgress } from '../../../shared/video-frame-progress';

export interface VideoJob {
  cameraStyleVersion?: 3 | 4;
  destination?: 'local' | 'remote';
  downloadFilename?: string | undefined;
  frameProgress?: VideoFrameProgress | undefined;
  placeTitle?: import('../../../renderer/src/place-title').PlaceTitle | undefined;
  exportRange?: import('../../../renderer/src/export-range').ExportRange | undefined;
  timelineDurationSeconds?: number | undefined;
  cinematicSequence?: CinematicSequence | undefined;
  depthOfField?: DepthOfFieldSettings | undefined;
  cameraGimbal?: import('../../../renderer/src/camera-gimbal').CameraGimbalId | undefined;
  cameraAngle?: number | undefined;
  trackAppearance?: TrackAppearance | undefined;
  generation?: VideoGenerationMetrics;
  presentation?: VideoPresentation | undefined;
  id: string;
  title: string;
  sourceFileName?: string | undefined;
  state: "processing" | "completed" | "failed" | "cancelled" | "queued" | "submitting" | "submission_failed" | "revoked";
  thumbnailUrl?: string;
  canCancel?: boolean;
  downloadUrl?: string | null;
  expiresAt?: string | null;
  accountId?: string;
  rideId?: string;
  attempts?: number;
  failureCode?: string | null;
  createdAt?: string;
  updatedAt?: string;
  phase: string;
  progress: number;
  detail: string;
  preset: string;
  durationSeconds: number;
  mapStyle: string;
  terrainExaggeration?: number | undefined;
  hidePlaces?: boolean | undefined;
  unitSystem?: "metric" | "imperial" | undefined;
  camera: CameraPlatformId;
  distance: CameraDistanceId;
  line: string;
}

export interface VideoRequest {
  cameraStyleVersion?: 3 | 4;
  placeTitle?: import('../../../renderer/src/place-title').PlaceTitle | undefined;
  exportRange?: import('../../../renderer/src/export-range').ExportRange | undefined;
  cinematicSequence?: CinematicSequence | undefined;
  depthOfField?: DepthOfFieldSettings | undefined;
  cameraGimbal?: import('../../../renderer/src/camera-gimbal').CameraGimbalId | undefined;
  cameraAngle?: number | undefined;
  trackAppearance?: TrackAppearance | undefined;
  presentation?: VideoPresentation | undefined;
  unitSystem?: "metric" | "imperial" | undefined;
  ride: Ride;
  preset: string;
  camera: string;
  distance: string;
  line: string;
  mapStyle: string;
  terrainExaggeration?: number | undefined;
  hidePlaces?: boolean | undefined;
  durationSeconds: number;
}

export interface LocalCapabilities {
  renderDestinations?: { local: boolean; remote: boolean };
  presentationVersions?: readonly number[];
  localRendering: boolean;
  localSignIn: boolean;
  hostedAlpha?: boolean;
  sharedVideos?: boolean;
}

export async function localCapabilities(): Promise<LocalCapabilities> {
  const response = await videoFetch("/api/local/capabilities");
  if (response.status === 404) return { localRendering: false, localSignIn: false };
  if (!response.ok) throw new Error('The video service is unavailable. Check the local server output.');
  if (response.headers.get('Content-Type')?.includes('text/html')) {
    throw new Error('This preview has no video service. Start the complete local app with npm run dev.');
  }
  const value = await response.json() as Partial<LocalCapabilities>;
  return { ...(value.renderDestinations ? { renderDestinations: value.renderDestinations } : {}), localRendering: value.localRendering === true, localSignIn: value.localSignIn === true,
    ...(Array.isArray(value.presentationVersions) ? { presentationVersions: value.presentationVersions.filter(version => version === 1 || version === 2) } : {}),
    ...(value.sharedVideos === true ? { sharedVideos: true } : {}), ...(value.hostedAlpha === true ? { hostedAlpha: true } : {}) };
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await videoFetch(path, options);
  const value: unknown = await response.json().catch(() => { throw new Error('The local video service returned an invalid response. Restart it with npm run dev.'); });
  if (!response.ok) {
    const error = (value as { error?: string | { message?: string } }).error;
    throw new Error((typeof error === 'string' ? error : error?.message) ?? 'The local render service could not complete the request.');
  }
  return value as T;
}

export function listVideos(): Promise<VideoJob[]> { return request("/api/local/renders"); }
export function localVideoConfiguration(id: string): Promise<{ ride: Ride; job: VideoJob }> {
  return request(`/api/local/renders/${encodeURIComponent(id)}/configuration`);
}
export function cancelVideo(id: string): Promise<VideoJob> {
  return request(`/api/local/renders/${id}`, { method: "DELETE" });
}
export function createVideo(input: VideoRequest): Promise<VideoJob> {
  const { id, title, region, source, sourceFileName, recordingDevice, timerDurationSeconds, points } = input.ride;
  return request("/api/local/renders", {
    method: "POST", headers: { "Content-Type": "application/json", [RENDER_RELEASE_HEADER]: releaseHeader(RENDER_RELEASE) },
    body: JSON.stringify({ ...input, ride: { id, title, region, source, sourceFileName, recordingDevice, timerDurationSeconds, points: input.ride.sourcePoints ?? points } }),
  });
}
